import { DetailedEstimationData, CategoryData, UserSelection } from '../types/estimation-items';

interface LegacyEstimation {
  description?: any;
  priceRange?: any;
  [categoryKey: string]: any;
}

interface AdaptationStats {
  totalItemsFound: number;
  totalItemsSelected: number;
  itemsByCategory: Record<string, number>;
  warnings: string[];
}

export class LegacyEstimationAdapter {
  private static instance: LegacyEstimationAdapter;
  private debugMode: boolean = true;
  private adaptationLog: Array<{type: string; message: string; data?: any}> = [];
  private stats: AdaptationStats = {
    totalItemsFound: 0,
    totalItemsSelected: 0,
    itemsByCategory: {},
    warnings: []
  };

  private constructor() {}

  public static getInstance(): LegacyEstimationAdapter {
    if (!LegacyEstimationAdapter.instance) {
      LegacyEstimationAdapter.instance = new LegacyEstimationAdapter();
    }
    return LegacyEstimationAdapter.instance;
  }

  private log(type: string, message: string, data?: any): void {
    if (this.debugMode) {
      this.adaptationLog.push({ type, message, data });
      console.log(`[LegacyAdapter] ${type}: ${message}`, data || '');
    }
  }

  private camelToSnake(str: string): string {
    return str.replace(/[A-Z]/g, letter => letter.toLowerCase()).toLowerCase();
  }

  private normalizeKey(key: string): string {
    return this.camelToSnake(key);
  }

  public adaptLegacyEstimation(legacyData: LegacyEstimation): DetailedEstimationData | any {
    this.adaptationLog = [];
    this.stats = {
      totalItemsFound: 0,
      totalItemsSelected: 0,
      itemsByCategory: {},
      warnings: []
    };

    this.log('INFO', '=== Starting legacy estimation adaptation ===');

    if (legacyData.type === 'quick' || this.isQuickEstimation(legacyData)) {
      this.log('INFO', 'Quick estimation detected - preserving original structure');
      return this.adaptQuickEstimation(legacyData);
    }

    if (legacyData.priceRange?.max) {
      this.log('INFO', `Original estimation total: ${legacyData.priceRange.max} €`);
    }

    const adapted: DetailedEstimationData = {
      description: this.adaptDescription(legacyData.description, legacyData.createdAt)
    };

    const categoryIds = [
      'preparation',
      'demolition',
      'gros-oeuvre',
      'vrd',
      'isolation',
      'cloisons',
      'technique',
      'sanitaire',
      'revetements',
      'menuiserie',
      'finitions',
      'exterieurs'
    ];

    categoryIds.forEach(categoryId => {
      if (legacyData[categoryId]) {
        this.log('INFO', `Processing category: ${categoryId}`);
        const categoryData = this.adaptCategoryData(legacyData[categoryId], categoryId);
        adapted[categoryId as keyof DetailedEstimationData] = categoryData as any;

        const itemCount = Object.keys(categoryData).filter(key => categoryData[key].selected).length;
        this.stats.itemsByCategory[categoryId] = itemCount;
        this.stats.totalItemsSelected += itemCount;
        this.stats.totalItemsFound += Object.keys(categoryData).length;

        this.log('INFO', `Category ${categoryId}: ${itemCount} items selected out of ${Object.keys(categoryData).length} total`,
          Object.keys(categoryData).filter(key => categoryData[key].selected));
      }
    });

    this.log('SUCCESS', `=== Adaptation complete ===`);
    this.log('SUCCESS', `Total items found: ${this.stats.totalItemsFound}`);
    this.log('SUCCESS', `Total items selected: ${this.stats.totalItemsSelected}`);

    if (this.stats.warnings.length > 0) {
      this.log('WARNING', `${this.stats.warnings.length} warnings found:`, this.stats.warnings);
    }

    if (this.debugMode) {
      console.log('=== ADAPTATION SUMMARY ===');
      console.log(`Total items found: ${this.stats.totalItemsFound}`);
      console.log(`Total items selected: ${this.stats.totalItemsSelected}`);
      console.log('Items by category:', this.stats.itemsByCategory);
      if (this.stats.warnings.length > 0) {
        console.log('Warnings:', this.stats.warnings);
      }
    }

    return adapted;
  }

  private isQuickEstimation(data: any): boolean {
    const hasQuickFields =
      data.propertyType !== undefined &&
      data.area !== undefined &&
      data.renovationLevel !== undefined;

    const detailedCategories = [
      'preparation', 'demolition', 'gros-oeuvre', 'vrd',
      'isolation', 'cloisons', 'technique', 'sanitaire',
      'revetements', 'menuiserie', 'finitions', 'exterieurs'
    ];

    const hasDetailedCategories = detailedCategories.some(
      category => data[category] !== undefined
    );

    return hasQuickFields && !hasDetailedCategories;
  }

  private adaptQuickEstimation(data: any): any {
    this.log('INFO', 'Adapting quick estimation');

    const adapted = {
      ...data,
      type: 'quick'
    };

    if (data.priceRange?.sections) {
      delete adapted.priceRange.sections;
    }

    this.log('SUCCESS', '=== Quick estimation adaptation complete ===');
    this.log('INFO', `Preserved fields: propertyType, area, location, renovationLevel, keyElements, priceRange`);

    return adapted;
  }

  private adaptDescription(description: any, createdAt?: string): any {
    if (!description) {
      return {};
    }

    const adapted = { ...description };

    if (!adapted.buildingAge) {
      adapted.buildingAge = this.inferBuildingAge(createdAt);
      this.log('INFO', `Inferred building age: ${adapted.buildingAge} years`);
    }

    if (!adapted.finishingLevel) {
      adapted.finishingLevel = 'standard';
      this.log('INFO', 'Set default finishing level: standard');
    }

    return adapted;
  }

  private inferBuildingAge(createdAt?: string): number {
    if (createdAt) {
      try {
        const estimationDate = new Date(createdAt);
        const currentYear = new Date().getFullYear();
        const estimationYear = estimationDate.getFullYear();

        const ageAtEstimation = 15;
        const yearsSinceEstimation = currentYear - estimationYear;

        return ageAtEstimation + yearsSinceEstimation;
      } catch (error) {
        console.warn('Error inferring building age:', error);
      }
    }

    return 15;
  }

  private adaptCategoryData(categoryData: any, categoryName: string): CategoryData {
    const adapted: CategoryData = {};
    this.processNestedStructure(categoryData, adapted, '', categoryName);
    return adapted;
  }

  private processNestedStructure(
    source: any,
    target: CategoryData,
    prefix: string,
    categoryName: string
  ): void {
    if (!source || typeof source !== 'object') {
      return;
    }

    for (const key in source) {
      const item = source[key];

      if (this.isUserSelection(item)) {
        const normalizedPrefix = prefix ? this.normalizeKey(prefix) : '';
        const normalizedKey = this.normalizeKey(key);
        const fullKey = normalizedPrefix ? `${normalizedPrefix}_${normalizedKey}` : normalizedKey;

        target[fullKey] = this.adaptUserSelection(item, fullKey, categoryName);

        if (item.selected) {
          this.log('DEBUG', `  ✓ Found selected item: ${fullKey}`, {
            value: item.value || item.quantity || item.surface,
            selected: item.selected
          });
        }
      }

      if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
        const newPrefix = prefix ? `${prefix}_${key}` : key;
        this.processNestedStructure(item, target, newPrefix, categoryName);
      }
    }
  }

  private isUserSelection(obj: any): boolean {
    if (!obj || typeof obj !== 'object') {
      return false;
    }

    return obj.hasOwnProperty('selected');
  }

  private adaptUserSelection(legacy: any, itemKey: string, categoryName: string): UserSelection {
    const adapted: UserSelection = {
      selected: legacy.selected === true
    };

    if (legacy.value !== undefined && legacy.value !== null) {
      adapted.value = Number(legacy.value);
    } else if (legacy.quantity !== undefined && legacy.quantity !== null) {
      adapted.value = Number(legacy.quantity);
      if (legacy.selected) {
        this.log('DEBUG', `    Using 'quantity' as value for ${itemKey}: ${legacy.quantity}`);
      }
    } else if (legacy.surface !== undefined && legacy.surface !== null) {
      adapted.value = Number(legacy.surface);
      if (legacy.selected) {
        this.log('DEBUG', `    Using 'surface' as value for ${itemKey}: ${legacy.surface}`);
      }
    } else if (legacy.selected) {
      adapted.value = 1;
      this.log('DEBUG', `    Defaulting to value=1 for selected item ${itemKey} with no explicit value`);
    }

    if (legacy.customAmount !== undefined && legacy.customAmount !== null) {
      adapted.customAmount = Number(legacy.customAmount);
    }

    if (legacy.selected && !adapted.value && adapted.value !== 0) {
      this.stats.warnings.push(`Item "${itemKey}" in category "${categoryName}" is selected but has no value/quantity/surface`);
      this.log('WARNING', `Item selected but no value found: ${categoryName}.${itemKey}`);
    }

    return adapted;
  }

  public getAdaptationStats(): AdaptationStats {
    return this.stats;
  }

  public getAdaptationLog(): Array<{type: string; message: string; data?: any}> {
    return this.adaptationLog;
  }

  public cleanupLegacyFields(data: any): any {
    const cleaned = { ...data };

    if (cleaned.priceRange?.sections) {
      delete cleaned.priceRange.sections;
    }

    const metaFields = ['id', 'createdAt', 'updatedAt', 'status', 'type', 'propertyId', 'comment', 'formatVersion', 'migratedAt', 'priceRange'];
    const cleanedData: any = {};

    metaFields.forEach(field => {
      if (cleaned[field] !== undefined) {
        cleanedData[field] = cleaned[field];
      }
    });

    if (cleaned.description) {
      cleanedData.description = cleaned.description;
    }

    const categoryIds = [
      'preparation', 'demolition', 'gros-oeuvre', 'vrd',
      'isolation', 'cloisons', 'technique', 'sanitaire',
      'revetements', 'menuiserie', 'finitions', 'exterieurs'
    ];

    categoryIds.forEach(categoryId => {
      if (cleaned[categoryId]) {
        cleanedData[categoryId] = cleaned[categoryId];
      }
    });

    return cleanedData;
  }
}

export const legacyAdapter = LegacyEstimationAdapter.getInstance();
